import React, { useState } from 'react';
import Layout from '../components/Layout';
import SEO from '../components/SEO';
import PageHeader from '../components/PageHeader';
import { CheckCircle2, ChevronRight } from 'lucide-react';
import { useConsultationModal } from '../context/ConsultationModalContext';

// Futuristic SVG Icons for Compliance
const IconCertification = () => (
    <svg viewBox="0 0 24 24" fill="none" className="w-full h-full">
        <circle cx="12" cy="9" r="6" stroke="currentColor" strokeWidth="1.5"/>
        <path d="M12 15V22" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        <path d="M9 19L12 22L15 19" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M9 12L11 14L15 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
);

const IconRegulatory = () => (
    <svg viewBox="0 0 24 24" fill="none" className="w-full h-full">
        <path d="M12 2L3 7V11C3 16.55 6.84 21.74 12 23C17.16 21.74 21 16.55 21 11V7L12 2Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
        <path d="M12 8V12M12 16H12.01" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
);

const IconSovereignty = () => (
    <svg viewBox="0 0 24 24" fill="none" className="w-full h-full">
        <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.5"/>
        <path d="M12 3C12 3 8 8 8 12C8 16 12 21 12 21" stroke="currentColor" strokeWidth="1.5"/>
        <path d="M12 3C12 3 16 8 16 12C16 16 12 21 12 21" stroke="currentColor" strokeWidth="1.5"/>
        <path d="M3 12H21" stroke="currentColor" strokeWidth="1.5"/>
        <circle cx="12" cy="12" r="3" fill="currentColor"/>
    </svg>
);

const IconAudit = () => (
    <svg viewBox="0 0 24 24" fill="none" className="w-full h-full">
        <rect x="4" y="3" width="16" height="18" rx="2" stroke="currentColor" strokeWidth="1.5"/>
        <path d="M8 8H16M8 12H16M8 16H12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        <circle cx="17" cy="17" r="3" stroke="currentColor" strokeWidth="1.5"/>
        <path d="M19 19L21 21" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
);

const IconGovernance = () => (
    <svg viewBox="0 0 24 24" fill="none" className="w-full h-full">
        <path d="M12 2L2 7H22L12 2Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
        <rect x="4" y="9" width="3" height="10" stroke="currentColor" strokeWidth="1.5"/>
        <rect x="10.5" y="9" width="3" height="10" stroke="currentColor" strokeWidth="1.5"/>
        <rect x="17" y="9" width="3" height="10" stroke="currentColor" strokeWidth="1.5"/>
        <path d="M2 19H22V22H2V19Z" stroke="currentColor" strokeWidth="1.5"/>
    </svg>
);

const IconIndustry = () => (
    <svg viewBox="0 0 24 24" fill="none" className="w-full h-full">
        <rect x="3" y="10" width="7" height="11" stroke="currentColor" strokeWidth="1.5"/>
        <rect x="14" y="3" width="7" height="18" stroke="currentColor" strokeWidth="1.5"/>
        <path d="M10 15L14 12V15" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
        <path d="M5 14H8M5 17H8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        <path d="M16 7H19M16 10H19M16 13H19M16 16H19" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
);

const Compliance = () => {
    const [activeSection, setActiveSection] = useState('certifications');
    const { openModal } = useConsultationModal();

    const sections = [
        { id: 'certifications', label: 'Certifications', Icon: IconCertification },
        { id: 'regulatory', label: 'Regulatory Alignment', Icon: IconRegulatory },
        { id: 'sovereignty', label: 'Data Sovereignty', Icon: IconSovereignty },
        { id: 'audit', label: 'Audit & Transparency', Icon: IconAudit },
        { id: 'governance', label: 'Operational Governance', Icon: IconGovernance },
        { id: 'industries', label: 'Regulated Industries', Icon: IconIndustry },
    ];

    return (
        <Layout>
            <SEO page="compliance" />
            <PageHeader
                title="Compliance" 
                highlight="& Governance"
                description="EverestCloud is built from the ground up to meet the most stringent regulatory, security, and governance requirements of Nepal's banking, government, and regulated industries."
            />

            <section className="py-16 bg-white dark:bg-slate-950 transition-colors duration-500">
                <div className="container mx-auto px-4">

                    {/* Section Navigation */}
                    <div className="flex flex-wrap justify-center gap-2 mb-12">
                        {sections.map((section) => (
                            <button
                                key={section.id}
                                onClick={() => setActiveSection(section.id)}
                                data-testid={`compliance-tab-${section.id}`}
                                className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-300 ${
                                    activeSection === section.id
                                        ? 'bg-accent text-black shadow-lg'
                                        : 'bg-slate-100 dark:bg-slate-800/50 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700'
                                }`}
                            >
                                <div className="w-4 h-4">
                                    <section.Icon />
                                </div>
                                {section.label}
                            </button>
                        ))}
                    </div>

                    {/* Content Sections */}
                    <div className="space-y-8">
                        
                        {/* Certifications & Standards */}
                        {activeSection === 'certifications' && (
                            <div className="animate-in fade-in-50 duration-500 space-y-8">
                                <ComplianceCard
                                    Icon={IconCertification}
                                    title="Certifications & Standards"
                                    subtitle="Globally Recognized Foundations"
                                    description="EverestCloud aligns with globally recognized standards to ensure a strong foundation for security, quality, and operational excellence."
                                />

                                <div className="grid md:grid-cols-3 gap-6">
                                    <CertificationCard 
                                        title="ISO 27001"
                                        subtitle="Information Security Management"
                                        description="Certified Information Security Management System (ISMS) ensuring robust controls for confidentiality, integrity, and availability of data across our platform."
                                        color="gold"
                                    />
                                    <CertificationCard 
                                        title="ISO 9001"
                                        subtitle="Quality Management"
                                        description="Certified Quality Management System (QMS) ensuring consistent service delivery, continual improvement, and customer satisfaction."
                                        color="blue"
                                    />
                                    <CertificationCard 
                                        title="PCI-DSS Aligned"
                                        subtitle="Payment Card Industry"
                                        description="Infrastructure and operational controls designed to support environments handling payment card data, enabling customers to meet PCI-DSS requirements."
                                        color="purple"
                                    />
                                </div>
                            </div>
                        )}

                        {/* Regulatory Alignment */}
                        {activeSection === 'regulatory' && (
                            <div className="animate-in fade-in-50 duration-500 space-y-8">
                                <ComplianceCard
                                    Icon={IconRegulatory}
                                    title="Regulatory Alignment"
                                    subtitle="National Framework Compliance"
                                    description="EverestCloud continuously monitors and aligns its policies, architecture, and operations with national regulatory frameworks and directives."
                                    leftTitle="Regulatory Bodies"
                                    leftItems={[
                                        "Nepal Rastra Bank (NRB) IT and Cybersecurity Guidelines",
                                        "Department of Information Technology (DoIT) policies",
                                        "Public Procurement Act & Regulations"
                                    ]}
                                    rightTitle="Customer Benefits"
                                    rightItems={[
                                        "Deploy regulated workloads with confidence",
                                        "Maintain compliance with local laws",
                                        "Meet supervisory expectations"
                                    ]}
                                />

                                <div className="bg-slate-50 dark:bg-slate-900/50 rounded-2xl border border-slate-200 dark:border-white/5 p-8">
                                    <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-6 text-center">Alignment with National Directives</h3>
                                    <div className="grid md:grid-cols-3 gap-6">
                                        <RegulatoryCard 
                                            title="Nepal Rastra Bank" 
                                            items={["IT Guidelines", "Cybersecurity Directives", "Risk Management Framework"]}
                                        />
                                        <RegulatoryCard 
                                            title="DoIT" 
                                            items={["Government IT Policies", "Data Center Standards", "Cloud Adoption Guidelines"]}
                                        />
                                        <RegulatoryCard 
                                            title="Public Sector" 
                                            items={["Procurement Act Compliance", "PSU Requirements", "Audit Standards"]}
                                        />
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Data Sovereignty & Residency */}
                        {activeSection === 'sovereignty' && (
                            <div className="animate-in fade-in-50 duration-500 space-y-8">
                                <ComplianceCard
                                    Icon={IconSovereignty}
                                    title="Data Sovereignty & Residency"
                                    subtitle="100% Nepal Data Residency"
                                    description="All customer data hosted on EverestCloud remains within Nepal. No data replication, processing, or administrative access occurs outside national borders unless explicitly authorized by the customer."
                                    leftTitle="Sovereignty Guarantees"
                                    leftItems={[
                                        "All data physically stored in Nepal",
                                        "No foreign jurisdiction exposure",
                                        "Local legal recourse available",
                                        "Complete data ownership retained"
                                    ]}
                                    rightTitle="Enables Compliance With"
                                    rightItems={[
                                        "Data localization requirements",
                                        "Banking sector residency mandates",
                                        "Government data sovereignty rules",
                                        "Jurisdictional control requirements"
                                    ]}
                                />

                                {/* Sovereignty Visual */}
                                <div className="bg-gradient-to-r from-accent/10 via-accent/5 to-transparent rounded-2xl border border-accent/20 p-8">
                                    <div className="flex flex-col md:flex-row items-center gap-8">
                                        <div className="w-24 h-24 rounded-2xl bg-accent/10 border border-accent/30 flex items-center justify-center text-accent shrink-0">
                                            <div className="w-12 h-12">
                                                <IconSovereignty />
                                            </div>
                                        </div>
                                        <div className="flex-1 text-center md:text-left">
                                            <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">Sovereign by Design</h3>
                                            <p className="text-slate-600 dark:text-slate-400 max-w-2xl">
                                                This sovereign design enables banks and public institutions to meet strict data-localization and jurisdictional requirements—ensuring your data never leaves Nepal.
                                            </p>
                                        </div>
                                        <div className="flex flex-wrap gap-3 justify-center md:justify-end">
                                            <SovereigntyBadge text="Nepal Only" />
                                            <SovereigntyBadge text="Zero Data Exit" />
                                            <SovereigntyBadge text="Local Control" />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Audit & Transparency */}
                        {activeSection === 'audit' && (
                            <div className="animate-in fade-in-50 duration-500 space-y-8">
                                <ComplianceCard
                                    Icon={IconAudit}
                                    title="Audit & Transparency"
                                    subtitle="Accountability at Every Level"
                                    description="Transparency and accountability are core to our compliance philosophy. We provide comprehensive tools and access for regulatory and internal audits."
                                />

                                <div className="grid md:grid-cols-3 gap-6">
                                    <AuditCard 
                                        title="Customer Audit Rights"
                                        items={[
                                            "Right to audit relevant controls",
                                            "Satisfy regulatory obligations",
                                            "Third-party audit support",
                                            "On-demand compliance evidence"
                                        ]}
                                        color="gold"
                                    />
                                    <AuditCard 
                                        title="Access & Change Logging"
                                        items={[
                                            "Comprehensive activity logging",
                                            "Immutable audit trails",
                                            "Who accessed what and when",
                                            "Forensic analysis ready"
                                        ]}
                                        color="blue"
                                    />
                                    <AuditCard 
                                        title="Disaster Recovery Evidence"
                                        items={[
                                            "Regular DR drills conducted",
                                            "RPO/RTO validation reports",
                                            "Evidence for regulators",
                                            "Documented recovery procedures"
                                        ]}
                                        color="purple"
                                    />
                                </div>

                                {/* Logging Visual */}
                                <div className="bg-slate-900 rounded-2xl border border-white/10 p-6 relative overflow-hidden">
                                    <div className="flex items-center gap-3 mb-6">
                                        <div className="flex gap-1.5">
                                            <div className="w-3 h-3 rounded-full bg-red-500"></div>
                                            <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                                            <div className="w-3 h-3 rounded-full bg-green-500"></div>
                                        </div>
                                        <span className="text-white/60 text-sm font-mono">audit.everestcloud.com.np</span>
                                    </div>
                                    <div className="space-y-2 font-mono text-sm">
                                        <LogEntry time="14:32:15" user="admin@bank.np" action="CONFIG_CHANGE" resource="firewall-rule-001" />
                                        <LogEntry time="14:31:42" user="ops@gov.np" action="ACCESS_GRANTED" resource="vm-prod-cluster" />
                                        <LogEntry time="14:30:18" user="audit@nrb.gov.np" action="REPORT_GENERATED" resource="compliance-q4-2025" />
                                        <LogEntry time="14:28:55" user="system" action="BACKUP_COMPLETED" resource="db-primary-001" />
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Operational Governance */}
                        {activeSection === 'governance' && (
                            <div className="animate-in fade-in-50 duration-500 space-y-8">
                                <ComplianceCard
                                    Icon={IconGovernance}
                                    title="Operational Governance"
                                    subtitle="Enterprise-Grade Controls"
                                    description="EverestCloud follows strict internal governance practices ensuring operational integrity and reduced systemic risk for regulated workloads."
                                    leftTitle="Governance Practices"
                                    leftItems={[
                                        "Role-based access control (RBAC)",
                                        "Segregation of duties",
                                        "Controlled change-management",
                                        "Continuous monitoring"
                                    ]}
                                    rightTitle="Risk Management"
                                    rightItems={[
                                        "Periodic risk assessments",
                                        "Incident response procedures",
                                        "Business continuity planning",
                                        "Vendor management controls"
                                    ]}
                                />

                                <div className="bg-slate-50 dark:bg-slate-900/50 rounded-2xl border border-slate-200 dark:border-white/5 p-8">
                                    <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-6 text-center">Governance Framework</h3>
                                    <div className="grid md:grid-cols-4 gap-4">
                                        <GovernanceCard title="Access Control" desc="RBAC with least privilege" icon="lock" />
                                        <GovernanceCard title="Separation of Duties" desc="Operations & admin segregated" icon="users" />
                                        <GovernanceCard title="Change Management" desc="Controlled & documented changes" icon="git" />
                                        <GovernanceCard title="Risk Assessment" desc="Continuous monitoring & review" icon="chart" />
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Built for Regulated Industries */}
                        {activeSection === 'industries' && (
                            <div className="animate-in fade-in-50 duration-500 space-y-8">
                                <ComplianceCard
                                    Icon={IconIndustry}
                                    title="Built for Regulated Industries"
                                    subtitle="Compliance-First Design"
                                    description="EverestCloud is trusted by organizations that cannot afford compliance gaps. Our compliance-first design enables customers to focus on innovation—while we handle governance complexity."
                                />

                                <div className="grid md:grid-cols-2 gap-6">
                                    <IndustryCard 
                                        title="Banking & Financial Services"
                                        items={["Commercial banks", "Development banks", "Financial institutions", "Payment service providers"]}
                                        color="gold"
                                    />
                                    <IndustryCard 
                                        title="Government & Public Sector"
                                        items={["Government agencies", "Public enterprises", "Utilities", "Regulatory bodies"]}
                                        color="blue"
                                    />
                                </div>

                                {/* Trust Banner */}
                                <div className="bg-gradient-to-r from-accent/10 via-accent/5 to-transparent rounded-2xl border border-accent/20 p-8 text-center">
                                    <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-3">Compliance Without Compromise</h3>
                                    <p className="text-slate-600 dark:text-slate-400 max-w-2xl mx-auto mb-6">
                                        Our cloud platform supports organizations operating under strict data-residency, auditability, and operational-resilience mandates—without compromise on performance or scalability.
                                    </p>
                                    <div className="flex flex-wrap justify-center gap-4">
                                        <TrustBadge text="Data Residency" />
                                        <TrustBadge text="Full Auditability" />
                                        <TrustBadge text="Operational Resilience" />
                                        <TrustBadge text="Regulatory Alignment" />
                                    </div>
                                </div>
                            </div>
                        )}

                    </div>

                    {/* Bottom CTA */}
                    <div className="mt-20 text-center">
                        <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white mb-4">
                            Ready for Compliance-Ready Cloud?
                        </h2>
                        <p className="text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto mb-8">
                            Talk to our compliance experts about how EverestCloud meets your regulatory requirements.
                        </p>
                        <button 
                            onClick={() => openModal('compliance_review')}
                            data-testid="compliance-cta-button"
                            className="inline-flex items-center gap-2 px-8 py-4 bg-accent text-black font-bold rounded-full hover:bg-accent/90 transition-all shadow-lg hover:shadow-xl hover:scale-105"
                        >
                            Request Compliance Review
                            <ChevronRight className="w-5 h-5" />
                        </button>
                    </div>

                </div>
            </section>
        </Layout>
    );
};

// Component: Main Compliance Card
const ComplianceCard = ({ Icon, title, subtitle, description, leftTitle, leftItems = [], rightTitle, rightItems = [] }) => (
    <div className="bg-white dark:bg-slate-900/60 rounded-2xl border border-slate-200 dark:border-white/10 p-8 relative overflow-hidden group hover:border-accent/30 transition-all duration-300">
        <div className="absolute top-0 right-0 w-48 h-48 bg-accent/5 rounded-full blur-[80px] pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity"></div>
        
        <div className="flex flex-col md:flex-row gap-8 relative z-10">
            <div className="md:w-1/3">
                <div className="w-14 h-14 rounded-xl bg-accent/10 border border-accent/20 flex items-center justify-center text-accent mb-4">
                    <div className="w-7 h-7">
                        <Icon />
                    </div>
                </div>
                <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">{title}</h3>
                <p className="text-accent font-medium text-sm uppercase tracking-wider mb-4">{subtitle}</p>
                {description && (
                    <p className="text-slate-600 dark:text-slate-400 leading-relaxed">{description}</p>
                )}
            </div>

            {(leftTitle || rightTitle) && (
                <div className="md:w-2/3 grid md:grid-cols-2 gap-8">
                    {leftTitle && (
                        <div>
                            <h4 className="text-sm font-bold text-slate-900 dark:text-white uppercase tracking-wider mb-4 flex items-center gap-2">
                                <span className="w-1.5 h-1.5 bg-accent rounded-full"></span>
                                {leftTitle}
                            </h4>
                            <ul className="space-y-2.5">
                                {leftItems.map((item, i) => (
                                    <li key={i} className="flex items-start gap-3 text-slate-600 dark:text-slate-300 text-sm">
                                        <CheckCircle2 className="w-4 h-4 text-accent shrink-0 mt-0.5" />
                                        <span>{item}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                    {rightTitle && (
                        <div>
                            <h4 className="text-sm font-bold text-slate-900 dark:text-white uppercase tracking-wider mb-4 flex items-center gap-2">
                                <span className="w-1.5 h-1.5 bg-blue-500 rounded-full"></span>
                                {rightTitle}
                            </h4>
                            <ul className="space-y-2.5">
                                {rightItems.map((item, i) => (
                                    <li key={i} className="flex items-start gap-3 text-slate-600 dark:text-slate-300 text-sm">
                                        <CheckCircle2 className="w-4 h-4 text-blue-500 shrink-0 mt-0.5" />
                                        <span>{item}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            )}
        </div>
    </div>
);

// Component: Certification Card
const CertificationCard = ({ title, subtitle, description, color }) => {
    const styles = {
        gold: { border: 'border-accent/30 hover:border-accent/50', badge: 'bg-accent/10 text-accent border-accent/20' },
        blue: { border: 'border-blue-500/30 hover:border-blue-500/50', badge: 'bg-blue-500/10 text-blue-500 border-blue-500/20' },
        purple: { border: 'border-purple-500/30 hover:border-purple-500/50', badge: 'bg-purple-500/10 text-purple-500 border-purple-500/20' }
    };
    const s = styles[color];
    
    return (
        <div className={`bg-white dark:bg-slate-900/60 rounded-xl border ${s.border} p-6 transition-all duration-300`}>
            <div className={`inline-block px-3 py-1 rounded-lg text-xs font-bold uppercase tracking-wider mb-4 border ${s.badge}`}>
                {title}
            </div>
            <h4 className="font-bold text-slate-900 dark:text-white mb-2">{subtitle}</h4>
            <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">{description}</p>
        </div>
    );
};

// Component: Regulatory Card
const RegulatoryCard = ({ title, items }) => (
    <div className="bg-white dark:bg-slate-900/60 rounded-xl border border-slate-200 dark:border-white/10 p-5">
        <h4 className="font-bold text-slate-900 dark:text-white mb-3 text-sm uppercase tracking-wider">{title}</h4>
        <ul className="space-y-2">
            {items.map((item, i) => (
                <li key={i} className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                    <div className="w-1 h-1 rounded-full bg-accent"></div>
                    {item}
                </li>
            ))}
        </ul>
    </div>
);

// Component: Sovereignty Badge
const SovereigntyBadge = ({ text }) => (
    <span className="px-4 py-2 bg-accent/10 border border-accent/30 rounded-full text-sm font-bold text-accent">
        {text}
    </span>
);

// Component: Audit Card
const AuditCard = ({ title, items, color }) => {
    const styles = {
        gold: { border: 'border-accent/30', dot: 'bg-accent' },
        blue: { border: 'border-blue-500/30', dot: 'bg-blue-500' },
        purple: { border: 'border-purple-500/30', dot: 'bg-purple-500' }
    };
    const s = styles[color];
    
    return (
        <div className={`bg-white dark:bg-slate-900/60 rounded-xl border ${s.border} p-6`}>
            <div className="flex items-center gap-2 mb-4">
                <div className={`w-2 h-2 rounded-full ${s.dot}`}></div>
                <h4 className="font-bold text-slate-900 dark:text-white">{title}</h4>
            </div>
            <ul className="space-y-2">
                {items.map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                        <CheckCircle2 className="w-3.5 h-3.5 text-green-500 shrink-0" />
                        {item}
                    </li>
                ))}
            </ul>
        </div>
    );
};

// Component: Log Entry
const LogEntry = ({ time, user, action, resource }) => {
    const actionColors = {
        CONFIG_CHANGE: 'text-yellow-400',
        ACCESS_GRANTED: 'text-green-400',
        REPORT_GENERATED: 'text-blue-400',
        BACKUP_COMPLETED: 'text-purple-400'
    };
    
    return (
        <div className="flex items-center gap-4 text-white/80">
            <span className="text-white/40">{time}</span>
            <span className="text-accent">{user}</span>
            <span className={actionColors[action] || 'text-white/60'}>{action}</span>
            <span className="text-white/60">{resource}</span>
        </div>
    );
};

// Component: Governance Card
const GovernanceCard = ({ title, desc }) => (
    <div className="bg-white dark:bg-slate-900/60 rounded-xl border border-slate-200 dark:border-white/10 p-5 text-center">
        <h4 className="font-bold text-slate-900 dark:text-white mb-2">{title}</h4>
        <p className="text-xs text-slate-500 dark:text-slate-400">{desc}</p>
    </div>
);

// Component: Industry Card
const IndustryCard = ({ title, items, color }) => {
    const styles = {
        gold: { border: 'border-accent/30', header: 'bg-accent/10 text-accent' },
        blue: { border: 'border-blue-500/30', header: 'bg-blue-500/10 text-blue-500' }
    };
    const s = styles[color];
    
    return (
        <div className={`bg-white dark:bg-slate-900/60 rounded-xl border ${s.border} overflow-hidden`}>
            <div className={`${s.header} px-6 py-4`}>
                <h4 className="font-bold text-lg">{title}</h4>
            </div>
            <div className="p-6">
                <ul className="space-y-3">
                    {items.map((item, i) => (
                        <li key={i} className="flex items-center gap-3 text-slate-600 dark:text-slate-300">
                            <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0" />
                            {item}
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    );
};

// Component: Trust Badge
const TrustBadge = ({ text }) => (
    <span className="px-4 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/10 rounded-full text-sm font-medium text-slate-700 dark:text-slate-300 shadow-sm">
        {text}
    </span>
);

export default Compliance;
